import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { KycStatus, KycStatusService, KycStatusSnapshot } from 'src/app/shared/services/kyc-status.service';
import { gatePassed } from 'src/app/shared/utils/compliance-exemption.util';

interface BannerStyleConfig {
  variant: 'progress' | 'review' | 'clarification' | 'rejected' | 'approved' | 'none';
  icon: string;
  title: string;
  subtitle: string;
  ctaLabel: string | null;
}

@Component({
  selector: 'app-kyc-status-banner',
  templateUrl: './kyc-status-banner.component.html',
  styleUrls: ['./kyc-status-banner.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KycStatusBannerComponent implements OnInit, OnDestroy {
  snap: KycStatusSnapshot | null = null;
  config: BannerStyleConfig = this.buildConfig('NOT_STARTED');
  private sub?: Subscription;

  constructor(
    private kycStatus: KycStatusService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.sub = this.kycStatus.status$.subscribe((snap) => {
      this.snap = snap;
      this.config = this.buildConfig(snap.status);
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /**
   * Author: Akarsh Singh | 04-08-2026
   * The applicant-role check lives HERE rather than in the host template, so
   * the banner can be dropped anywhere without leaking. The status service
   * seeds a NOT_STARTED snapshot and never refreshes for admins, so without
   * this check an admin would be told to complete a KYC they never have.
   */
  get isVisible(): boolean {
    if (!this.kycStatus.isApplicantRole()) {
      return false;
    }
    return !!this.snap && this.snap.loadState === 'SUCCESS' && !gatePassed(this.snap);
  }

  goToKyc() {
    this.router.navigateByUrl('/settings/kyc');
  }

  private buildConfig(status: KycStatus): BannerStyleConfig {
    switch (status) {
      case 'NOT_STARTED':
        return {
          variant: 'progress',
          icon: 'assignment',
          title: 'Complete your KYC verification',
          subtitle: 'Start your verification to unlock the full marketplace experience.',
          ctaLabel: 'Start KYC',
        };
      case 'IN_PROGRESS':
        return {
          variant: 'progress',
          icon: 'edit_note',
          title: 'KYC in progress',
          subtitle: 'Continue filling your details to submit for review.',
          ctaLabel: 'Resume',
        };
      case 'SUBMITTED':
      case 'UNDER_REVIEW':
        return {
          variant: 'review',
          icon: 'pending',
          title: 'KYC verification in progress',
          subtitle: 'Admin review typically takes 2 to 3 business days. We\'ll notify you once it\'s approved.',
          ctaLabel: 'View status',
        };
      case 'CLARIFICATION_REQUIRED':
        return {
          variant: 'clarification',
          icon: 'priority_high',
          title: 'Clarification needed',
          subtitle: 'Admin has requested additional information. Open your KYC to review and respond.',
          ctaLabel: 'Open KYC',
        };
      case 'REJECTED':
        return {
          variant: 'rejected',
          icon: 'cancel',
          title: 'KYC rejected',
          subtitle: 'Your application was rejected. Reopen it to update details and resubmit.',
          ctaLabel: 'Reopen & resubmit',
        };
      case 'APPROVED':
      default:
        return {
          variant: 'none',
          icon: 'check_circle',
          title: '',
          subtitle: '',
          ctaLabel: null,
        };
    }
  }
}
